import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyPlansApi } from 'features/company-plans/companyPlans.api';
import {
  ENTITLEMENT_STATUSES,
  companyDisplayName,
  displayCredit,
  entitlementActionAllowed,
  invalidateCompanyPlanQueries,
  listItems,
  listMeta,
  planDisplayName,
} from 'features/company-plans/companyPlans.helpers';
import { handleMutationError, StatusChip } from 'features/company-plans/CompanyPlanUi';
import { Button } from 'shared/components/Button';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { FilterBar } from 'shared/components/FilterBar';
import { ListTable } from 'shared/components/ListTable';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { useToast } from 'shared/components/Toast';
import { SelectField, TextField } from 'shared/forms/Fields';
import { formatDateTime } from 'shared/utilities/format';

export function EntitlementsPage() {
  const { t, dir } = useI18n();
  const [filters, setFilters] = useState({ page: 1, per_page: 15, company_id: '', status: '' });
  const query = useQuery({
    queryKey: queryKeys.companyEntitlements(filters),
    queryFn: ({ signal }) => companyPlansApi.listEntitlements(filters, { signal }),
  });
  const items = listItems(query.data);
  const meta = listMeta(query.data);

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader title={t('companyPlans.entitlementsTitle')} />
      <FilterBar resetLabel={t('actions.reset')} onReset={() => setFilters({ page: 1, per_page: 15, company_id: '', status: '' })}>
        <TextField id="entitlement-company" label={t('companyPlans.companyName')} value={filters.company_id} onChange={(value) => setFilters((current) => ({ ...current, company_id: value, page: 1 }))} />
        <SelectField
          id="entitlement-status"
          label={t('companyPlans.status')}
          value={filters.status}
          onChange={(value) => setFilters((current) => ({ ...current, status: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...ENTITLEMENT_STATUSES.map((status) => ({ value: status, label: t(`companyPlans.statuses.${status}`) }))]}
        />
      </FilterBar>
      {items.length === 0 ? <EmptyState title={t('empty.entitlements')} /> : (
        <ListTable caption={t('companyPlans.entitlementsTitle')} stickyActions>
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('companyPlans.companyName')}</th>
              <th>{t('companyPlans.purchases')}</th>
              <th>{t('companyPlans.availableCredits')}</th>
              <th>{t('companyPlans.status')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{companyDisplayName(item.company)}</td>
                <td>{item.purchase_id ? `#${item.purchase_id}` : '—'}</td>
                <td>{displayCredit(item, 'available_credits')}</td>
                <td><StatusChip status={item.status} t={t} /></td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${item.id}`}
                  items={[detailsAction(paths.companyEntitlementDetail(item.id), t)]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function EntitlementDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.entitlementsManage);
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.companyEntitlement(id),
    queryFn: ({ signal }) => companyPlansApi.showEntitlement(id, { signal }),
  });

  const transition = useMutation({
    mutationFn: (action) => {
      if (action === 'suspend') return companyPlansApi.suspendEntitlement(id);
      if (action === 'resume') return companyPlansApi.restoreEntitlement(id);
      return companyPlansApi.revokeEntitlement(id);
    },
    onSuccess: async () => {
      await invalidateCompanyPlanQueries(queryClient);
      toast.success(t('status.updated'));
    },
    onError: (error) => handleMutationError(error, { t, toast }),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  const entitlement = query.data;
  const run = async (action, title, body, tone = 'danger') => {
    if (transition.isPending) return;
    if (await confirm({ title, body, tone })) {
      transition.mutate(action);
    }
  };

  return (
    <div>
      <PageHeader
        title={`${t('companyPlans.entitlementDetail')} #${entitlement.id}`}
        backTo={paths.companyEntitlements}
        actions={canManage ? (
          <>
            {entitlementActionAllowed(entitlement, 'suspend') ? (
              <Button tone="ghost" loading={transition.isPending} onClick={() => run('suspend', t('companyPlans.suspendEntitlement'), t('companyPlans.suspendEntitlementBody'))}>
                {t('actions.suspend')}
              </Button>
            ) : null}
            {entitlementActionAllowed(entitlement, 'resume') ? (
              <Button loading={transition.isPending} onClick={() => run('resume', t('companyPlans.resumeEntitlement'), t('companyPlans.resumeEntitlementBody'), 'primary')}>
                {t('actions.resume')}
              </Button>
            ) : null}
            {entitlementActionAllowed(entitlement, 'revoke') ? (
              <Button tone="danger" loading={transition.isPending} onClick={() => run('revoke', t('companyPlans.revokeEntitlement'), t('companyPlans.revokeEntitlementBody'))}>
                {t('actions.revoke')}
              </Button>
            ) : null}
          </>
        ) : null}
      />
      <dl className="definition-grid">
        <div className="definition-row"><dt>{t('companyPlans.companyName')}</dt><dd>{entitlement.company ? <Link to={paths.companyDetail(entitlement.company.id)}>{companyDisplayName(entitlement.company)}</Link> : '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.purchases')}</dt><dd>{entitlement.purchase_id ? <Link to={paths.planPurchaseDetail(entitlement.purchase_id)}>#{entitlement.purchase_id}</Link> : '—'}</dd></div>
        <div className="definition-row"><dt>{t('nav.companyPlans')}</dt><dd>{planDisplayName(entitlement.plan, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.totalCredits')}</dt><dd>{displayCredit(entitlement, 'total_credits')}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.availableCredits')}</dt><dd>{displayCredit(entitlement, 'available_credits')}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.reservedCredits')}</dt><dd>{displayCredit(entitlement, 'reserved_credits')}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.consumedCredits')}</dt><dd>{displayCredit(entitlement, 'consumed_credits')}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.status')}</dt><dd><StatusChip status={entitlement.status} t={t} /></dd></div>
        <div className="definition-row"><dt>{t('companyPlans.validityPeriod')}</dt><dd>{formatDateTime(entitlement.activated_at, locale)} → {formatDateTime(entitlement.expires_at, locale)}</dd></div>
      </dl>
    </div>
  );
}
